import { Box, Chip, Icon, Menu, MenuItem, Typography } from '@shared/ui';
import React from 'react';

export type ArtifactVersionOption = {
  revision: number;
  updated_at: string;
};

type ArtifactVersionDropdownProps = {
  typeLabel: string;
  currentRevision: number;
  versions: ArtifactVersionOption[];
  onVersionSelect: (revision: number) => void;
  disabled?: boolean;
  pendingGeneration?: boolean;
};

export const ArtifactVersionDropdown = ({
  typeLabel,
  currentRevision,
  versions,
  onVersionSelect,
  disabled = false,
  pendingGeneration = false,
}: ArtifactVersionDropdownProps): React.ReactElement => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const latestRevision = versions.length > 0 ? versions[0].revision : currentRevision;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled && versions.length > 0) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleVersionSelect = (revision: number) => {
    handleClose();
    if (revision !== currentRevision) {
      onVersionSelect(revision);
    }
  };

  if (pendingGeneration) {
    return (
      <Typography variant="body1" sx={{ fontSize: '1.125rem', fontWeight: 500 }}>
        {typeLabel}
      </Typography>
    );
  }

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: disabled || versions.length === 0 ? 'default' : 'pointer',
          userSelect: 'none',
          '&:hover': disabled || versions.length === 0 ? {} : { opacity: 0.8 },
        }}
      >
        <Typography variant="body1" sx={{ fontSize: '1.125rem', fontWeight: 500 }}>
          {`${typeLabel} · V${currentRevision}`}
        </Typography>
        <Icon fontSize="small">expand_more</Icon>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {versions.map((version) => (
          <MenuItem
            key={version.revision}
            onClick={() => handleVersionSelect(version.revision)}
            selected={version.revision === currentRevision}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography>V{version.revision}</Typography>
              {version.revision === latestRevision ? (
                <Chip label="Latest" size="small" color="primary" />
              ) : null}
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
